<template>
  <div class="find-rep col xs6 l4-9 critical-component-clear">
    <h1
      class="find-rep__title text-heading-2xl text-heading-4xl-desktop"
      v-text="title"
    />

    <div class="find-rep__controls">
      <p
        class="text-body-s-regular"
        v-html="headerText"
      />

      <div class="find-rep__types">
        <btn
          class="find-rep__type"
          :class="{[$class('active')]: repType === 'uk'}"
          :label="$string('general.find_rep.uk_type')"
          modifiers="secondary block center"
          @click="updateRepType('uk')"
        />
        <btn
          class="find-rep__type"
          :class="{[$class('active')]: repType !== 'uk'}"
          :label="$string('general.find_rep.int_type')"
          modifiers="secondary block center"
          @click="updateRepType('int')"
        />
      </div>

      <div
        class="find-rep__selects-wrapper"
        :class="{'find-rep__selects-wrapper--single': repType !== 'uk'}"
      >
        <transition name="fade">
          <div
            v-show="repType === 'uk'"
            class="find-rep__selects"
          >
            <vue-select
              :options="schoolTypesOptions"
              class="find-rep__select"
              :class="{[$class('error')]: showFillSchool}"
              name="schoolType"
              :label="$string('general.find_rep.school_type_label')"
              :selected-index="schoolTypeIndex"
              @change="(index) => updateSelected(index, 'school')"
            >
              <template #toggle="{ selectedOption }">
                <span
                  class="text-body-s-regular find-rep__select-label"
                  :class="{[$class('disabled')]: !selectedOption?.value}"
                  v-text="selectedOption?.name"
                />

                <icon-chevron-fill-down />
              </template>

              <template
                v-for="(item, index) of schoolTypesOptions"
                #[`option${index}`]
                :key="item.value"
              >
                <span
                  class="text-body-s-bold find-rep__select-option"
                  :class="{[$class('disabled')]: !item.value}"
                  v-text="item.name"
                />
              </template>
            </vue-select>

            <vue-select
              :options="authoritiesOptions"
              class="find-rep__select"
              :class="{[$class('error')]: showFillAuthority}"
              name="authority"
              :label="$string('general.find_rep.authority_label')"
              :selected-index="authorityIndex"
              @change="(index) => updateSelected(index, 'authority')"
            >
              <template #toggle="{ selectedOption }">
                <span
                  class="text-body-s-regular find-rep__select-label"
                  :class="{[$class('disabled')]: !selectedOption?.value}"
                  v-text="selectedOption?.name"
                />

                <icon-chevron-fill-down />
              </template>

              <template
                v-for="(item, index) of authoritiesOptions"
                #[`option${index}`]
                :key="item.value"
              >
                <span
                  class="text-body-s-bold find-rep__select-option"
                  :class="{[$class('disabled')]: !item.value}"
                  v-text="item.name"
                />
              </template>
            </vue-select>
          </div>
        </transition>

        <transition name="fade">
          <div
            v-show="repType !== 'uk'"
            class="find-rep__selects"
          >
            <vue-select
              :options="countriesOptions"
              class="find-rep__select"
              :class="{[$class('error')]: showFillCountry}"
              name="country"
              :label="$string('general.find_rep.country_label')"
              :selected-index="countryIndex"
              @change="(index) => updateSelected(index, 'country')"
            >
              <template #toggle="{ selectedOption }">
                <span
                  class="text-body-s-regular find-rep__select-label"
                  :class="{[$class('disabled')]: !selectedOption?.value}"
                  v-text="selectedOption?.name"
                />

                <icon-chevron-fill-down />
              </template>

              <template
                v-for="(item, index) of countriesOptions"
                #[`option${index}`]
                :key="item.value"
              >
                <span
                  class="text-body-s-bold find-rep__select-option"
                  :class="{[$class('disabled')]: !item.value}"
                  v-text="item.name"
                />
              </template>
            </vue-select>
          </div>
        </transition>
      </div>

      <p
        v-if="showFillForm"
        class="find-rep__error text-body-s-regular"
        v-text="$string('general.find_rep.fill_form')"
      />

      <btn
        :label="$string('general.find_rep.search_label')"
        modifiers="primary block center"
        @click="showReps"
      />

      <btn
        v-if="formSubmitted && !showFillForm"
        :label="$string('general.find_rep.no_rep.label')"
        class-label="text-body-s-underline"
        modifiers="text"
        @click="toggleOverlay({
          ignoreDismissed: true,
          namespace: 'noRepFound',
        })"
      />
      <p
        v-else
        class="text-body-s-regular"
        v-html="footerText"
      />
    </div>

    <transition-height>
      <div
        v-show="formSubmitted"
        class="find-rep__list"
      >
        <contact-card
          v-for="(rep, index) of selectedReps"
          :key="index"
          :rep="rep"
          :region="repType === 'uk' ? authority : country"
          @callback="handleRepCallback"
        />

        <p
          v-if="selectedReps.length === 0 && !showFillForm"
          class="text-body-s-regular"
          v-html="noResultsInstructions"
        />
      </div>
    </transition-height>

    <!-- Rep callback form modal -->
    <overlay
      key="repCallback"
      block-class="rep-callback-form-overlay"
      direction="none"
      namespace="repCallback"
      type="shelf-modal"
    >
      <template #body>
        <rep-callback-form
          :rep="selectedRep"
        />
      </template>
    </overlay>

    <!-- No rep found modal -->
    <overlay
      key="noRepFound"
      block-class="find-rep-overlay"
      direction="none"
      namespace="noRepFound"
      type="shelf-modal"
    >
      <template #title>
        {{ $string('general.find_rep.no_rep.label') }}
      </template>

      <template #body>
        <p
          class="text-body-s-regular"
          v-html="modalCopy"
        />

        <btn
          :label="$string('general.find_rep.no_rep.close_label')"
          modifiers="secondary block center"
          @click="toggleOverlay({
            ignoreDismissed: true,
            namespace: 'noRepFound',
          })"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Find a rep (find-rep)
 * -----------------------------------------------------------------------------
 * Find a Rep section, used in Find a Rep page.
 *
 * @param {String} title - Page title.
 * @param {String} headerUk - Section's Header UK.
 * @param {String} footerUk - Section's Footer UK.
 * @param {String} headerInt - Section's Header Int.
 * @param {String} footerInt - Section's Footer Int.
 * @param {String} modalCopy - Section's Modal Copy.
 * @param {Array} reps - Section's Liquid ID.
 *
 */

import { mapActions } from 'vuex'
import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import VueSelect from '~async/vue-select/vue-select'
import ContactCard from '~async/contact-card/contact-card'
import RepCallbackForm from '~async/rep-callback-form/rep-callback-form'
import TransitionHeight from '~global/core/transition-height'
import IconChevronFillDown from '~icons/directional-navigation/chevron-fill/down.svg'

export default {
  name: 'FindRep',

  components: {
    Btn,
    Overlay,
    VueSelect,
    ContactCard,
    RepCallbackForm,
    TransitionHeight,
    IconChevronFillDown,
  },

  props: {
    title: {
      type: String,
      default: () => '',
    },
    headerUk: {
      type: String,
      default: () => '',
    },
    footerUk: {
      type: String,
      default: () => '',
    },
    headerInt: {
      type: String,
      default: () => '',
    },
    footerInt: {
      type: String,
      default: () => '',
    },
    modalCopy: {
      type: String,
      default: () => '',
    },
    noResultsInstructions: {
      type: String,
      default: () => '',
    },
    reps: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // Rep type, either 'uk' or 'int'
      repType: 'uk',
      schoolTypeIndex: 0,
      schoolType: '',
      authorityIndex: 0,
      authority: '',
      countryIndex: 0,
      country: '',
      selectedReps: [],
      formSubmitted: false,
      selectedRep: null,
    }
  },

  computed: {
    headerText() {
      if (this.repType === 'uk') {
        return this.headerUk
      }

      return this.headerInt
    },

    footerText() {
      if (this.repType === 'uk') {
        return this.footerUk
      }

      return this.footerInt
    },

    schoolTypes() {
      const repsSchoolTypes = this.reps.map(rep => rep?.schoolType || []).flat()
      return [...new Set(repsSchoolTypes)].sort()
    },

    schoolTypesOptions() {
      const data = this.schoolTypes.map(option => {
        return {
          name: option,
          value: option,
        }
      })

      return [
        {
          name: this.$string('general.find_rep.school_type_label'),
          value: '',
        },
        ...data,
      ]
    },

    authorities() {
      const repsAuthorities = this.reps.map(rep => rep?.authority || []).flat()
      return [...new Set(repsAuthorities)].sort()
    },

    authoritiesOptions() {
      const data = this.authorities.map(option => {
        return {
          name: option,
          value: option,
        }
      })

      return [
        {
          name: this.$string('general.find_rep.authority_label'),
          value: '',
        },
        ...data,
      ]
    },

    countries() {
      const repsCountries = this.reps
        .map(rep => rep?.country || [])
        .flat()
        .filter(country => country !== this.$string('general.find_rep.uk_country'))

      return [...new Set(repsCountries)].sort()
    },

    countriesOptions() {
      const data = this.countries.map(option => {
        return {
          name: option,
          value: option,
        }
      })

      return [
        {
          name: this.$string('general.find_rep.country_label'),
          value: '',
        },
        ...data,
      ]
    },

    showFillSchool() {
      return this.formSubmitted && !this.schoolType
    },

    showFillAuthority() {

      return this.formSubmitted && !this.authority
    },

    showFillCountry() {
      return this.formSubmitted && !this.country
    },

    showFillForm() {
      if (this.repType === 'uk') {
        return this.showFillSchool || this.showFillAuthority
      }

      return this.showFillCountry
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    updateSelected(index, type) {
      this.formSubmitted = false

      switch (type) {
        case 'school':
          this.schoolTypeIndex = index
          this.schoolType = this.schoolTypes[index - 1]
          break
        case 'authority':
          this.authorityIndex = index
          this.authority = this.authorities[index - 1]
          break
        case 'country':
          this.countryIndex = index
          this.country = this.countries[index - 1]
          break
        default:
          break
      }
    },

    showReps() {
      this.selectedReps = this.reps.filter(rep => {
        if (this.repType === 'uk') {
          return rep.authority.includes(this.authority) &&
            rep.schoolType.includes(this.schoolType)
        }

        return rep.country.includes(this.country)
      })

      this.formSubmitted = true
    },

    updateRepType(type) {
      this.repType = type
      this.formSubmitted = false
    },

    handleRepCallback(rep) {
      this.selectedRep = rep

      if (!this.selectedRep) {
        return
      }

      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: 'repCallback',
      })
    },
  },
}
</script>

<style lang="scss">
@import './find-rep';
</style>
